import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, Legend } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LogOut, LayoutDashboard, Euro, ShieldAlert, Users, TrendingUp, Crown, Repeat, Percent, LifeBuoy, Send, Loader2, Search, MessageSquare, Download, Gift, RotateCcw, Cpu, Settings, Receipt, Save, User, Trash2, Zap, ClipboardList } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function Stat({ label, value, accent }) {
  return (
    <div className="gt-card p-5">
      <div className="text-xs uppercase tracking-widest text-white/40 mb-2">{label}</div>
      <div className={`font-mono-clock text-2xl font-bold ${accent}`}>{value}</div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="gt-card p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-[12px] flex items-center justify-center ${accent}`}><Icon size={20} /></div>
      <div>
        <div className="text-xs uppercase tracking-widest text-white/40">{label}</div>
        <div className="font-mono-clock text-xl font-bold text-white">{value}</div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [finance, setFinance] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expStart, setExpStart] = useState("");
  const [expEnd, setExpEnd] = useState("");
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [limitsOpen, setLimitsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const load = async () => {
    try {
      const [f, u, tk] = await Promise.all([api.get("/admin/finance"), api.get("/admin/users"), api.get("/admin/tickets")]);
      setFinance(f.data);
      setUsers(u.data);
      setTickets(tk.data);
      try { const ai = await api.get("/admin/ai-usage"); setAiUsage(ai.data); } catch { /* ignore */ }
      try { const s = await api.get("/admin/settings"); setSettings(s.data); } catch { /* ignore */ }
    } catch { toast.error("Erreur de chargement"); }
  };

  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings(data);
      toast.success("Réglages enregistrés");
      setSettingsOpen(false);
      load();
    } catch { toast.error("Erreur"); }
    finally { setSavingSettings(false); }
  };

  const openDetail = async (userId) => {
    setDetailOpen(true);
    setDetail(null);
    try { const { data } = await api.get(`/admin/user/${userId}`); setDetail(data); }
    catch { toast.error("Erreur"); setDetailOpen(false); }
  };

  const downloadInvoice = async (paymentId) => {
    try {
      const res = await api.get(`/payments/invoice/${paymentId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch { toast.error("Facture indisponible"); }
  };

  const killSwitch = async (userId) => {
    try {
      await api.post(`/admin/kill-switch/${userId}`);
      toast.success("Accès révoqué");
      load();
    } catch { toast.error("Erreur"); }
  };

  const openTicket = async (tk) => {
    setActiveTicket(tk);
    try { await api.post(`/admin/tickets/${tk.id}/read`); load(); } catch { /* ignore */ }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/admin/tickets/${activeTicket.id}/reply`, { message: reply });
      setReply("");
      const { data } = await api.get("/admin/tickets");
      setTickets(data);
      setActiveTicket(data.find((x) => x.id === activeTicket.id) || null);
    } catch { toast.error("Erreur"); }
    finally { setSending(false); }
  };

  const closeTicket = async () => {
    try {
      await api.post(`/admin/tickets/${activeTicket.id}/close`);
      toast.success("Ticket fermé");
      const { data } = await api.get("/admin/tickets");
      setTickets(data);
      setActiveTicket(data.find((x) => x.id === activeTicket.id) || null);
    } catch { toast.error("Erreur"); }
  };

  const eur = (n) => `${(n ?? 0).toFixed(2)} €`;
  const eur4 = (n) => `${(n ?? 0).toFixed(4)} €`;

  const downloadCsv = async (path, filename) => {
    try {
      const params = {};
      if (expStart) params.start = expStart;
      if (expEnd) params.end = expEnd;
      const res = await api.get(path, { responseType: "blob", params });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Erreur lors de l'export"); }
  };

  const reactivateUser = async (userId) => {
    try { await api.post(`/admin/reactivate/${userId}`); toast.success("Client réactivé"); load(); }
    catch { toast.error("Erreur"); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Supprimer définitivement le compte "${u.email}" ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Compte supprimé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  const offerPack = async (userId) => {
    try {
      const { data } = await api.post(`/admin/grant-bonus/${userId}`, { amount: 150 });
      toast.success(`Pack offert · +150 messages (total bonus : ${data.coach_bonus})`);
      load();
    } catch { toast.error("Erreur"); }
  };

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      const matchQ = !q || (u.email || "").toLowerCase().includes(q) || `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase().includes(q);
      const matchStatus =
        statusFilter === "all" ||
        (statusFilter === "lifetime" && u.is_paid) ||
        (statusFilter === "coach" && u.is_coach_active) ||
        (statusFilter === "free" && !u.is_paid && !u.is_coach_active);
      return matchQ && matchStatus;
    });
  }, [users, search, statusFilter]);

  const groupCounts = useMemo(() => ({
    all: users.length,
    free: users.filter((u) => !u.is_paid && !u.is_coach_active).length,
    lifetime: users.filter((u) => u.is_paid).length,
    coach: users.filter((u) => u.is_coach_active).length,
  }), [users]);

  const groupTabs = [
    { key: "all", label: "Tous", accent: "text-white border-white/40 bg-white/10" },
    { key: "free", label: "Non payants", accent: "text-white/60 border-white/30 bg-white/10" },
    { key: "lifetime", label: "À vie", accent: "text-[#007BFF] border-[#007BFF]/50 bg-[#007BFF]/15" },
    { key: "coach", label: "Abonnés", accent: "text-[#00E676] border-[#00E676]/50 bg-[#00E676]/15" },
  ];

  const marginTable = useMemo(() => {
    const s = settings || {};
    // token-based pricing (EUR/token) — same rates as backend accounting
    const TOK = {
      standard: { in: 0.00000092, out: 0.0000046 }, // Claude Haiku 4.5
      super: { in: 0.00000276, out: 0.0000138 },     // Claude Sonnet 4.6
    };
    const IN_TOK = 1900, OUT_TOK = 300; // avg tokens/message (system + history 20 + user, + reply)
    const TOK_PER_MSG = IN_TOK + OUT_TOK;
    const msgCost = (k) => IN_TOK * TOK[k].in + OUT_TOK * TOK[k].out;
    const TTS_COST = 0.006;
    const DAYS = 30;
    const tva = (p) => +(p - p / 1.2).toFixed(2);
    const stripe = (p) => +(p * 0.015 + 0.25).toFixed(2);
    const sub = (key, label, price, msgDay, ttsDay) => {
      const tokens = msgDay * DAYS * TOK_PER_MSG;
      const aiMax = +(msgDay * DAYS * msgCost(key) + ttsDay * DAYS * TTS_COST).toFixed(2);
      const t = tva(price), st = stripe(price);
      const net = +(price - t - st - aiMax).toFixed(2);
      return { key, label, price, quota: `${msgDay} msg/j · ${ttsDay} voix/j`, tokens, aiMax, tva: t, stripe: st, net, kind: "sub" };
    };
    const pack = (key, label, price, msgs) => {
      const tokens = msgs * TOK_PER_MSG;
      const aiMax = +(msgs * msgCost(key)).toFixed(2);
      const t = tva(price), st = stripe(price);
      const net = +(price - t - st - aiMax).toFixed(2);
      return { key: `pack_${key}`, label, price, quota: `${msgs} msgs`, tokens, aiMax, tva: t, stripe: st, net, kind: "pack" };
    };
    const pPack = s.price_pack ?? 2.0, nPack = s.pack_messages ?? 80;
    return [
      sub("standard", "Coach Standard /mois", s.price_coach ?? 9.99, s.daily_message_limit ?? 40, s.tts_daily_limit ?? 5),
      sub("super", "Super IA /mois", s.price_super ?? 19.99, s.daily_message_limit_super ?? 40, s.tts_daily_limit_super ?? 5),
      pack("standard", "Pack (client Standard)", pPack, nPack),
      pack("super", "Pack (client Super IA)", pPack, nPack),
    ];
  }, [settings]);

  const resetApp = async () => {
    const c = window.prompt("⚠️ RÉINITIALISATION TOTALE\n\nCeci supprime TOUS les utilisateurs (sauf admin), paiements, factures, abonnements, messages et tickets. Action IRRÉVERSIBLE.\n\nTape RESET en majuscules pour confirmer :");
    if (c !== "RESET") { if (c !== null) toast.error("Confirmation incorrecte, annulé"); return; }
    try {
      const { data } = await api.post("/admin/reset", { confirm: "RESET" });
      toast.success(`Réinitialisé · ${data.deleted_users} comptes supprimés`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la réinitialisation");
    }
  };

  const deleteAllFree = async () => {
    if (!window.confirm(`Supprimer TOUS les comptes non payants (${groupCounts.free}) ? Action irréversible. Les clients payants ne sont pas touchés.`)) return;
    try {
      const { data } = await api.post("/admin/delete-free-users");
      toast.success(`${data.deleted} comptes non payants supprimés`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const unreadTickets = tickets.filter((t) => t.admin_unread).length;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grain">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 min-h-16 py-2 flex items-center justify-between gap-2 flex-wrap">
          <div className="font-display text-xl font-extrabold flex items-center gap-2">
            Gaim<span className="text-[#007BFF]">Time</span>
            <span className="text-xs bg-[#FF3B30]/20 text-[#FF3B30] px-2 py-0.5 rounded-full ml-2">ADMIN</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="admin-limits-btn" onClick={() => setLimitsOpen(true)} variant="outline" className="border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10 rounded-[12px] px-3" title="Limites & Marges">
              <ClipboardList size={16} className="sm:mr-1.5" /> <span className="hidden sm:inline">Limites & Marges</span>
            </Button>
            <Button data-testid="admin-reset-btn" onClick={resetApp} variant="outline" className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-[12px] px-3" title="Reset">
              <Trash2 size={16} className="sm:mr-1.5" /> <span className="hidden sm:inline">Reset</span>
            </Button>
            <Button data-testid="admin-settings-btn" onClick={() => setSettingsOpen(true)} variant="outline" className="border-white/10 text-white hover:bg-white/5 rounded-[12px] px-3" title="Réglages">
              <Settings size={16} className="sm:mr-1.5" /> <span className="hidden sm:inline">Réglages</span>
            </Button>
            <Button data-testid="admin-dashboard-btn" onClick={() => navigate("/dashboard")} variant="outline" className="border-[#007BFF]/40 text-[#007BFF] hover:bg-[#007BFF]/10 rounded-[12px] px-3" title="Ouvrir l'application">
              <LayoutDashboard size={16} className="sm:mr-1.5" /> <span className="hidden sm:inline">Application</span>
            </Button>
            <Button data-testid="admin-logout-btn" onClick={() => { logout(); navigate("/"); }} variant="outline" className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-[12px] px-3" title="Déconnexion">
              <LogOut size={16} className="sm:mr-1.5" /> <span className="hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8 space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-extrabold mb-1 flex items-center gap-2"><Euro className="text-[#00E676]" /> Vue financière</h1>
            <p className="text-white/40 text-sm">{finance?.nb_ventes ?? 0} ventes enregistrées</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col">
              <label className="text-[10px] uppercase tracking-widest text-white/30 mb-1">Du</label>
              <Input type="date" data-testid="export-start-date" value={expStart} onChange={(e) => setExpStart(e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white rounded-[12px] h-9 w-36 text-xs" />
            </div>
            <div className="flex flex-col">
              <label className="text-[10px] uppercase tracking-widest text-white/30 mb-1">Au</label>
              <Input type="date" data-testid="export-end-date" value={expEnd} onChange={(e) => setExpEnd(e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white rounded-[12px] h-9 w-36 text-xs" />
            </div>
            <Button data-testid="export-clients-btn" onClick={() => downloadCsv("/admin/export/clients.csv", "gaimtime-clients.csv")} variant="outline" className="border-white/10 text-white hover:bg-white/5 rounded-[12px] h-9 text-xs">
              <Download size={14} className="mr-1.5" /> Clients (CSV)
            </Button>
            <Button data-testid="export-payments-btn" onClick={() => downloadCsv("/admin/export/payments.csv", "gaimtime-paiements.csv")} variant="outline" className="border-white/10 text-white hover:bg-white/5 rounded-[12px] h-9 text-xs">
              <Download size={14} className="mr-1.5" /> Paiements (CSV)
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Stat label="CA Brut" value={eur(finance?.ca_brut)} accent="text-white" />
          <Stat label="TVA (20%)" value={`- ${eur(finance?.tva)}`} accent="text-white/70" />
          <Stat label="Frais Stripe" value={`- ${eur(finance?.frais_stripe)}`} accent="text-white/70" />
          <Stat label="Coût API" value={`- ${eur(finance?.cout_api)}`} accent="text-white/70" />
          <Stat label="Gain net réel" value={eur(finance?.gain_net)} accent="text-[#00E676] text-glow-mint" />
        </div>

        <div>
          <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2 text-white/80"><TrendingUp size={16} className="text-[#007BFF]" /> Métriques business</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <MetricCard icon={Crown} label="Clients à vie" value={finance?.nb_lifetime ?? 0} accent="bg-[#007BFF]/15 text-[#007BFF]" />
            <MetricCard icon={Repeat} label="Abonnés Coach" value={finance?.nb_subscribers ?? 0} accent="bg-[#00E676]/15 text-[#00E676]" />
            <MetricCard icon={Zap} label="Abonnés Super IA" value={finance?.nb_super ?? 0} accent="bg-[#00E676]/15 text-[#00E676]" />
            <MetricCard icon={Euro} label="MRR (récurrent/mois)" value={eur(finance?.mrr)} accent="bg-[#00E676]/15 text-[#00E676]" />
            <MetricCard icon={Percent} label="Conversion" value={`${finance?.conversion ?? 0} %`} accent="bg-white/10 text-white/80" />
          </div>
        </div>

        <div data-testid="admin-ai-usage">
          <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2 text-white/80"><Cpu size={16} className="text-[#00E676]" /> Consommation IA</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <MetricCard icon={MessageSquare} label="Messages IA (total)" value={aiUsage?.total_messages ?? 0} accent="bg-[#00E676]/15 text-[#00E676]" />
            <MetricCard icon={MessageSquare} label="Messages IA (aujourd'hui)" value={aiUsage?.today_messages ?? 0} accent="bg-[#007BFF]/15 text-[#007BFF]" />
            <MetricCard icon={Euro} label="Coût API réel (total)" value={eur4(aiUsage?.cost_total)} accent="bg-white/10 text-white/80" />
            <MetricCard icon={Euro} label="Coût API (aujourd'hui)" value={eur4(aiUsage?.cost_today)} accent="bg-white/10 text-white/80" />
          </div>
          <div className="gt-card p-6">
            <div className="text-xs uppercase tracking-widest text-white/40 mb-1">Consommation par client (Standard {eur4(aiUsage?.cost_per_msg?.standard)} · Super {eur4(aiUsage?.cost_per_msg?.super)} /msg est.)</div>
            <div className="text-[11px] text-white/50 mb-4" data-testid="ai-tokens-summary">Tokens consommés — Total : <b className="text-[#00E676]">{(aiUsage?.tokens_total ?? 0).toLocaleString()}</b> · Aujourd'hui : <b className="text-[#007BFF]">{(aiUsage?.tokens_today ?? 0).toLocaleString()}</b></div>
            {aiUsage?.per_user?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-ai-usage-table">
                  <thead>
                    <tr className="text-white/40 text-xs uppercase tracking-widest text-left border-b border-white/10">
                      <th className="py-2 pr-3 font-medium">Client</th>
                      <th className="py-2 px-3 font-medium text-right">Aujourd'hui</th>
                      <th className="py-2 px-3 font-medium text-right">Total msg</th>
                      <th className="py-2 px-3 font-medium text-right">Tokens</th>
                      <th className="py-2 pl-3 font-medium text-right">Coût réel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aiUsage.per_user.map((r) => (
                      <tr key={r.user_id} data-testid={`ai-usage-row-${r.user_id}`} className="border-b border-white/5 last:border-0">
                        <td className="py-2.5 pr-3">
                          <div className="font-medium text-white/90 truncate max-w-[180px] flex items-center gap-1.5">
                            {r.name}
                            {r.is_admin && <span className="text-[9px] uppercase bg-[#007BFF]/20 text-[#007BFF] px-1.5 py-0.5 rounded-full">admin</span>}
                          </div>
                          <div className="text-white/40 text-xs truncate max-w-[180px]">{r.email}</div>
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono-clock text-white/70">{r.today}</td>
                        <td className="py-2.5 px-3 text-right font-mono-clock text-white">{r.total}</td>
                        <td className="py-2.5 px-3 text-right font-mono-clock text-white/70">{(r.tokens ?? 0).toLocaleString()}</td>
                        <td className="py-2.5 pl-3 text-right font-mono-clock text-white/70">{eur4(r.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="text-white/30 text-sm py-8 text-center">Aucune consommation IA pour l'instant.</div>}
          </div>
        </div>

        <div className="gt-card p-6">
          <div className="text-xs uppercase tracking-widest text-white/40 mb-4 flex items-center gap-2"><TrendingUp size={14} /> Bénéfice par mois (Revenus − Coût IA)</div>
          {finance?.histogram?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={finance.histogram} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <XAxis dataKey="month" stroke="#A0A0A0" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#A0A0A0" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,0.03)" }} contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }}
                  formatter={(v, n) => [`${v} €`, n === "revenue" ? "Revenus" : n === "ai_cost" ? "Coût IA" : "Bénéfice net"]} />
                <Legend formatter={(v) => (v === "revenue" ? "Revenus" : v === "ai_cost" ? "Coût IA" : "Bénéfice net")} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="revenue" fill="#007BFF" radius={[6, 6, 0, 0]} />
                <Bar dataKey="ai_cost" fill="#FF3B30" radius={[6, 6, 0, 0]} />
                <Bar dataKey="net" fill="#00E676" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-white/30 text-sm py-10 text-center">Aucune donnée pour l'instant.</div>}
        </div>

        {/* Tickets */}
        <div className="gt-card p-6">
          <div className="text-xs uppercase tracking-widest text-white/40 mb-4 flex items-center gap-2">
            <LifeBuoy size={14} /> Tickets de support ({tickets.length})
            {unreadTickets > 0 && <span className="ml-1 text-[10px] bg-[#FF3B30] text-white px-2 py-0.5 rounded-full">{unreadTickets} non lus</span>}
          </div>
          {tickets.length === 0 ? (
            <div className="text-white/30 text-sm py-8 text-center">Aucun ticket pour l'instant.</div>
          ) : (
            <div className="space-y-2" data-testid="admin-tickets-list">
              {tickets.map((tk) => (
                <button key={tk.id} data-testid={`admin-ticket-${tk.id}`} onClick={() => openTicket(tk)}
                  className="w-full text-left flex items-center justify-between gap-3 p-3 rounded-[12px] bg-[#0A0A0A] border border-white/5 hover:border-white/20 transition-all">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {tk.admin_unread && <span className="w-2 h-2 rounded-full bg-[#FF3B30] shrink-0" />}
                      <span className="font-semibold truncate">{tk.subject}</span>
                    </div>
                    <div className="text-xs text-white/40 truncate">{tk.user_email}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-white/40 text-xs flex items-center gap-1"><MessageSquare size={12} /> {tk.messages?.length || 0}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${tk.status === "open" ? "bg-[#00E676]/20 text-[#00E676]" : "bg-white/10 text-white/50"}`}>
                      {tk.status === "open" ? "Ouvert" : "Fermé"}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Users */}
        <div className="gt-card p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
            <div className="text-xs uppercase tracking-widest text-white/40 flex items-center gap-2"><Users size={14} /> Gestion clients ({filteredUsers.length})</div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <Input data-testid="admin-user-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher email / nom" className="bg-[#0A0A0A] border-white/10 text-white rounded-[12px] h-9 pl-8 w-56" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {groupTabs.map((t) => (
              <button
                key={t.key}
                data-testid={`admin-group-${t.key}`}
                onClick={() => setStatusFilter(t.key)}
                className={`flex items-center gap-2 px-3.5 h-9 rounded-[12px] text-sm border transition-colors ${statusFilter === t.key ? t.accent : "text-white/50 border-white/10 bg-transparent hover:bg-white/5"}`}
              >
                {t.label}
                <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${statusFilter === t.key ? "bg-black/30" : "bg-white/10"}`}>{groupCounts[t.key]}</span>
              </button>
            ))}
            {statusFilter === "free" && groupCounts.free > 0 && (
              <Button data-testid="admin-delete-all-free-btn" onClick={deleteAllFree} variant="outline"
                className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-[12px] h-9 text-xs ml-auto">
                <Trash2 size={14} className="mr-1.5" /> Supprimer tous les non-payants ({groupCounts.free})
              </Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead>
                <tr className="text-white/40 text-xs uppercase border-b border-white/5">
                  <th className="text-left py-2 font-medium">Client</th>
                  <th className="text-left py-2 font-medium">Email</th>
                  <th className="text-left py-2 font-medium">Statut</th>
                  <th className="text-left py-2 font-medium">Facturation</th>
                  <th className="text-right py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="border-b border-white/5" data-testid={`admin-user-${u.id}`}>
                    <td className="py-3">{`${u.first_name} ${u.last_name}`.trim() || "—"}</td>
                    <td className="py-3 text-white/60">{u.email}</td>
                    <td className="py-3">
                      <div className="flex gap-1.5 flex-wrap">
                        {u.is_paid && <span className="text-[10px] bg-[#007BFF]/20 text-[#007BFF] px-2 py-0.5 rounded-full">À vie</span>}
                        {u.is_coach_active && <span className="text-[10px] bg-[#00E676]/20 text-[#00E676] px-2 py-0.5 rounded-full">Abonné</span>}
                        {!u.is_paid && !u.is_coach_active && <span className="text-[10px] bg-white/10 text-white/50 px-2 py-0.5 rounded-full">Gratuit</span>}
                        {u.is_admin && <span className="text-[10px] bg-[#FF3B30]/20 text-[#FF3B30] px-2 py-0.5 rounded-full">Admin</span>}
                      </div>
                    </td>
                    <td className="py-3 font-mono-clock text-white/60">{eur((u.payments || []).reduce((s, p) => s + p.amount_ttc, 0))}</td>
                    <td className="py-3 text-right">
                      <div className="flex gap-1.5 justify-end">
                        <Button data-testid={`user-detail-${u.id}`} onClick={() => openDetail(u.id)} variant="outline"
                          className="border-white/10 text-white/70 hover:bg-white/5 rounded-[12px] h-8 text-xs" title="Fiche client">
                          <User size={13} className="mr-1" /> Détails
                        </Button>
                        {!u.is_admin && (
                          <>
                            {u.is_coach_active && (
                              <Button data-testid={`offer-pack-${u.id}`} onClick={() => offerPack(u.id)} variant="outline"
                                className="border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10 rounded-[12px] h-8 text-xs" title="Offrir +150 messages Coach">
                                <Gift size={13} className="mr-1" /> Pack
                              </Button>
                            )}
                            {(!u.is_paid && !u.is_coach_active) ? (
                              <>
                              <Button data-testid={`reactivate-${u.id}`} onClick={() => reactivateUser(u.id)} variant="outline"
                                className="border-[#007BFF]/40 text-[#007BFF] hover:bg-[#007BFF]/10 rounded-[12px] h-8 text-xs">
                                <RotateCcw size={13} className="mr-1" /> Réactiver
                              </Button>
                              <Button data-testid={`delete-user-${u.id}`} onClick={() => deleteUser(u)} variant="outline"
                                title="Supprimer ce compte non payant"
                                className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-[12px] h-8 px-2 text-xs">
                                <Trash2 size={13} />
                              </Button>
                              </>
                            ) : (
                              <Button data-testid={`kill-switch-${u.id}`} onClick={() => killSwitch(u.id)} variant="outline"
                                className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-[12px] h-8 text-xs">
                                <ShieldAlert size={13} className="mr-1" /> Kill
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Ticket thread modal */}
      <Dialog open={!!activeTicket} onOpenChange={(v) => !v && setActiveTicket(null)}>
        <DialogContent className="bg-[#111111] border border-white/10 text-white rounded-[16px] max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold flex items-center gap-2">
              <LifeBuoy className="text-[#00E676]" size={20} /> {activeTicket?.subject}
            </DialogTitle>
          </DialogHeader>
          {activeTicket && (
            <>
              <div className="text-xs text-white/40">{activeTicket.user_name} · {activeTicket.user_email}</div>
              <div className="flex-1 overflow-y-auto space-y-3 py-2 pr-1" data-testid="admin-ticket-thread">
                {activeTicket.messages.map((m, i) => (
                  <div key={i} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-[14px] px-3.5 py-2.5 text-sm ${m.sender === "admin" ? "bg-[#007BFF] text-white" : "bg-[#0A0A0A] border border-white/10 text-white/90"}`}>
                      <div className="text-[10px] uppercase tracking-widest opacity-60 mb-1">{m.sender === "admin" ? "GaimTime" : activeTicket.user_name}</div>
                      {m.content}
                    </div>
                  </div>
                ))}
              </div>
              {activeTicket.status === "open" ? (
                <form onSubmit={sendReply} className="flex gap-2 pt-2 border-t border-white/5">
                  <Input data-testid="admin-ticket-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Ta réponse…" className="bg-[#0A0A0A] border-white/10 text-white rounded-[12px] h-11" />
                  <Button data-testid="admin-ticket-reply-submit" type="submit" disabled={sending} className="bg-[#007BFF] hover:bg-[#0056b3] rounded-[12px] px-4">
                    {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                  </Button>
                  <Button type="button" data-testid="admin-ticket-close" onClick={closeTicket} variant="outline" className="border-white/10 text-white/60 hover:bg-white/5 rounded-[12px] h-11 text-xs">Fermer</Button>
                </form>
              ) : (
                <div className="text-center text-white/40 text-sm pt-2 border-t border-white/5">Ticket fermé</div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings modal */}
      <Dialog open={limitsOpen} onOpenChange={setLimitsOpen}>
        <DialogContent className="bg-[#111111] border border-[#00E676]/30 text-white rounded-[16px] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="limits-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold flex items-center gap-2">
              <ClipboardList className="text-[#00E676]" size={22} /> Limites & Marges par abonnement
            </DialogTitle>
          </DialogHeader>
          <p className="text-white/50 text-sm">Marge nette si un abonné/client consomme TOUT son quota (pire cas). Déductions réelles : TVA 20% · Stripe (1,5% + 0,25 €) · coût IA. Chiffres estimatifs, lus depuis tes Réglages.</p>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-sm" data-testid="limits-table">
              <thead>
                <tr className="text-white/40 text-[10px] uppercase tracking-widest text-left border-b border-white/10">
                  <th className="py-2 pr-2 font-medium">Formule</th>
                  <th className="py-2 px-2 font-medium text-right">Prix</th>
                  <th className="py-2 px-2 font-medium text-right">Quota</th>
                  <th className="py-2 px-2 font-medium text-right">Tokens (est.)</th>
                  <th className="py-2 px-2 font-medium text-right">−TVA</th>
                  <th className="py-2 px-2 font-medium text-right">−Stripe</th>
                  <th className="py-2 px-2 font-medium text-right">−Coût IA</th>
                  <th className="py-2 pl-2 font-medium text-right">= NET</th>
                </tr>
              </thead>
              <tbody>
                {marginTable.map((r) => (
                  <tr key={r.key} data-testid={`limits-row-${r.key}`} className={`border-b border-white/5 last:border-0 ${r.kind === "pack" ? "bg-white/[0.02]" : ""}`}>
                    <td className="py-3 pr-2">
                      <div className="font-semibold flex items-center gap-1.5">
                        {r.key === "super" && <Zap size={13} className="text-[#00E676]" />}{r.label}
                      </div>
                    </td>
                    <td className="py-3 px-2 text-right font-mono-clock text-white">{r.price.toFixed(2)} €</td>
                    <td className="py-3 px-2 text-right text-white/60 text-xs">{r.quota}</td>
                    <td className="py-3 px-2 text-right font-mono-clock text-white/50 text-xs">{r.tokens.toLocaleString()}</td>
                    <td className="py-3 px-2 text-right font-mono-clock text-white/50">{r.tva.toFixed(2)}</td>
                    <td className="py-3 px-2 text-right font-mono-clock text-white/50">{r.stripe.toFixed(2)}</td>
                    <td className="py-3 px-2 text-right font-mono-clock text-[#FF3B30]">{r.aiMax.toFixed(2)}</td>
                    <td className={`py-3 pl-2 text-right font-mono-clock font-bold ${r.net >= 0 ? "text-[#00E676]" : "text-[#FF3B30]"}`}>{r.net.toFixed(2)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-white/40 mt-2 space-y-1">
            <div>• « Coût IA » calculé AU TOKEN (~2 200 tokens/message : contexte + réponse) aux tarifs réels : Haiku entrée 0,92 €/M · sortie 4,60 €/M · Sonnet entrée 2,76 €/M · sortie 13,80 €/M.</div>
            <div>• « NET » = ce qu'il te reste après TVA, Stripe et IA, dans le PIRE cas (quota 100% utilisé). En usage réel (3-5× moins), ta marge est bien supérieure.</div>
            <div>• Voix (TTS) estimée ~0,006 €/lecture. Le comptage admin des tokens inclut aussi les messages du pack.</div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="bg-[#111111] border border-white/10 text-white rounded-[16px] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold flex items-center gap-2"><Settings className="text-[#00E676]" size={20} /> Réglages</DialogTitle>
          </DialogHeader>
          {settings && (
            <div className="space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Coach IA — Limites / jour</div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-xs text-white/60">Messages Standard</label><Input data-testid="settings-daily-limit" type="number" value={settings.daily_message_limit}
                    onChange={(e) => setSettings((s) => ({ ...s, daily_message_limit: parseInt(e.target.value || "0", 10) }))}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-[12px] h-10 mt-1" /></div>
                  <div><label className="text-xs text-[#00E676]/80">Messages Super IA</label><Input data-testid="settings-daily-limit-super" type="number" value={settings.daily_message_limit_super}
                    onChange={(e) => setSettings((s) => ({ ...s, daily_message_limit_super: parseInt(e.target.value || "0", 10) }))}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-[12px] h-10 mt-1" /></div>
                  <div><label className="text-xs text-white/60">Voix TTS Standard</label><Input data-testid="settings-tts-limit" type="number" value={settings.tts_daily_limit}
                    onChange={(e) => setSettings((s) => ({ ...s, tts_daily_limit: parseInt(e.target.value || "0", 10) }))}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-[12px] h-10 mt-1" /></div>
                  <div><label className="text-xs text-[#00E676]/80">Voix TTS Super IA</label><Input data-testid="settings-tts-limit-super" type="number" value={settings.tts_daily_limit_super}
                    onChange={(e) => setSettings((s) => ({ ...s, tts_daily_limit_super: parseInt(e.target.value || "0", 10) }))}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-[12px] h-10 mt-1" /></div>
                </div>
                <label className="text-xs text-white/60 mt-2 block">Messages offerts par pack</label>
                <Input data-testid="settings-pack-messages" type="number" value={settings.pack_messages}
                  onChange={(e) => setSettings((s) => ({ ...s, pack_messages: parseInt(e.target.value || "0", 10) }))}
                  className="bg-[#0A0A0A] border-white/10 text-white rounded-[12px] h-10 mt-1" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Prix (€)</div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-xs text-white/60">À vie</label><Input data-testid="settings-price-lifetime" type="number" step="0.01" value={settings.price_lifetime} onChange={(e) => setSettings((s) => ({ ...s, price_lifetime: parseFloat(e.target.value || "0") }))} className="bg-[#0A0A0A] border-white/10 text-white rounded-[12px] h-10 mt-1" /></div>
                  <div><label className="text-xs text-white/60">Coach/mois</label><Input data-testid="settings-price-coach" type="number" step="0.01" value={settings.price_coach} onChange={(e) => setSettings((s) => ({ ...s, price_coach: parseFloat(e.target.value || "0") }))} className="bg-[#0A0A0A] border-white/10 text-white rounded-[12px] h-10 mt-1" /></div>
                  <div><label className="text-xs text-[#00E676]/80">Super IA/mois</label><Input data-testid="settings-price-super" type="number" step="0.01" value={settings.price_super} onChange={(e) => setSettings((s) => ({ ...s, price_super: parseFloat(e.target.value || "0") }))} className="bg-[#0A0A0A] border-white/10 text-white rounded-[12px] h-10 mt-1" /></div>
                  <div><label className="text-xs text-white/60">Pack</label><Input data-testid="settings-price-pack" type="number" step="0.01" value={settings.price_pack} onChange={(e) => setSettings((s) => ({ ...s, price_pack: parseFloat(e.target.value || "0") }))} className="bg-[#0A0A0A] border-white/10 text-white rounded-[12px] h-10 mt-1" /></div>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-1.5"><Receipt size={12} /> Infos facture</div>
                {[["company_name", "Raison sociale"], ["company_address", "Adresse"], ["company_siret", "SIRET"], ["company_vat", "N° TVA"], ["invoice_prefix", "Préfixe facture"], ["invoice_legal", "Mention légale (bas de facture)"]].map(([k, lbl]) => (
                  <div key={k} className="mt-2">
                    <label className="text-xs text-white/60">{lbl}</label>
                    <Input data-testid={`settings-${k}`} value={settings[k] || ""} onChange={(e) => setSettings((s) => ({ ...s, [k]: e.target.value }))} className="bg-[#0A0A0A] border-white/10 text-white rounded-[12px] h-10 mt-1" />
                  </div>
                ))}
              </div>
              <Button data-testid="settings-save-btn" onClick={saveSettings} disabled={savingSettings} className="w-full bg-[#00E676] hover:bg-[#00c765] text-black rounded-[12px] h-11 font-semibold">
                {savingSettings ? <Loader2 className="animate-spin" /> : <><Save size={16} className="mr-1.5" /> Enregistrer</>}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* User detail modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-[#111111] border border-white/10 text-white rounded-[16px] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="user-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold flex items-center gap-2"><User className="text-[#007BFF]" size={20} /> Fiche client</DialogTitle>
          </DialogHeader>
          {!detail ? <div className="py-10 text-center text-white/40"><Loader2 className="animate-spin inline" /></div> : (
            <div className="space-y-4">
              <div>
                <div className="font-semibold text-white flex items-center gap-2 flex-wrap">
                  {`${detail.user.first_name || ""} ${detail.user.last_name || ""}`.trim() || "—"}
                  {detail.is_coach_active && detail.coach_tier === "super" && <span className="text-[9px] uppercase bg-gradient-to-r from-[#00E676]/30 to-[#007BFF]/30 text-[#00E676] px-1.5 py-0.5 rounded-full font-bold">Super IA</span>}
                  {detail.is_coach_active && detail.coach_tier !== "super" && <span className="text-[9px] uppercase bg-[#00E676]/20 text-[#00E676] px-1.5 py-0.5 rounded-full">Abonné Coach</span>}
                  {detail.is_paid && <span className="text-[9px] uppercase bg-[#007BFF]/20 text-[#007BFF] px-1.5 py-0.5 rounded-full">À vie</span>}
                  {(!detail.is_paid && !detail.is_coach_active) && <span className="text-[9px] uppercase bg-white/10 text-white/50 px-1.5 py-0.5 rounded-full">Gratuit</span>}
                </div>
                <div className="text-white/50 text-sm">{detail.user.email}</div>
                <div className="text-white/40 text-xs mt-1">Langue : {(detail.user.language || "fr").toUpperCase()}</div>
              </div>

              <div className="gt-card p-4" data-testid="detail-quota">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-widest text-white/40">Quota IA du jour</span>
                  <span className="font-mono-clock text-white">{detail.used_today} / {detail.effective_limit ?? detail.daily_limit}</span>
                </div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-[#00E676] rounded-full" style={{ width: `${Math.min(100, (detail.used_today / ((detail.effective_limit ?? detail.daily_limit) || 1)) * 100)}%` }} />
                </div>
                <div className="text-xs text-white/50 mt-2 flex justify-between">
                  <span>Restant aujourd'hui : <b className="text-white/80">{detail.remaining}</b></span>
                  <span data-testid="detail-bonus">Solde packs offerts : <b className="text-[#00E676]">{detail.bonus}</b></span>
                </div>
                <div className="text-[10px] text-white/30 mt-1">Base {detail.daily_limit}/jour + {detail.bonus} packs reportables</div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="gt-card p-3"><div className="text-[10px] uppercase text-white/40">Facturé</div><div className="font-mono-clock text-[#00E676]">{eur(detail.total_billed)}</div></div>
                <div className="gt-card p-3"><div className="text-[10px] uppercase text-white/40">Msgs IA total</div><div className="font-mono-clock">{detail.ai_total}</div></div>
                <div className="gt-card p-3" data-testid="detail-ai-cost"><div className="text-[10px] uppercase text-white/40">Coût API réel</div><div className="font-mono-clock text-white/80">{eur4(detail.ai_cost)}</div><div className="text-[9px] text-white/30">{(detail.ai_tokens ?? 0).toLocaleString()} tokens</div></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Paiements & factures</div>
                {detail.payments.length ? detail.payments.map((p) => (
                  <div key={p.id} data-testid={`detail-payment-${p.id}`} className="flex items-center justify-between py-2 border-b border-white/5">
                    <div><div className="text-sm text-white/90">{p.label}</div><div className="text-white/40 text-xs">{(p.created_at || "").slice(0, 10)} · {eur(p.amount_ttc)}</div></div>
                    <Button data-testid={`download-invoice-${p.id}`} onClick={() => downloadInvoice(p.id)} variant="outline" className="border-[#007BFF]/40 text-[#007BFF] hover:bg-[#007BFF]/10 rounded-[12px] h-8 text-xs">
                      <Receipt size={13} className="mr-1" /> Facture
                    </Button>
                  </div>
                )) : <div className="text-white/30 text-sm py-2">Aucun paiement.</div>}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Packs offerts (bonus)</div>
                {detail.grants && detail.grants.length ? detail.grants.map((g) => (
                  <div key={g.id} data-testid={`detail-grant-${g.id}`} className="flex items-center justify-between py-1.5 border-b border-white/5 text-sm">
                    <span className="text-[#00E676]">+{g.amount} messages</span>
                    <span className="text-white/40 text-xs">{(g.created_at || "").slice(0, 10)} · {g.granted_by}</span>
                  </div>
                )) : <div className="text-white/30 text-sm py-2">Aucun pack offert.</div>}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Tickets ({detail.tickets.length})</div>
                {detail.tickets.length ? detail.tickets.map((t) => (
                  <div key={t.id} className="text-sm py-1.5 border-b border-white/5 flex justify-between">
                    <span className="text-white/80 truncate">{t.subject}</span>
                    <span className={`text-xs ${t.status === "open" ? "text-[#00E676]" : "text-white/40"}`}>{t.status === "open" ? "Ouvert" : "Fermé"}</span>
                  </div>
                )) : <div className="text-white/30 text-sm py-2">Aucun ticket.</div>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
